"use client";

import { useState, memo } from "react";
import type { BlindScheduleDto, BlindLevelDto } from "@/lib/texas-holdem-types";

interface BlindScheduleConfigProps {
  value: BlindScheduleDto | null;
  onChange: (schedule: BlindScheduleDto | null) => void;
}

const PRESETS: Record<string, BlindLevelDto[]> = {
  quick: [
    { smallBlind: 5, bigBlind: 10 },
    { smallBlind: 10, bigBlind: 20 },
    { smallBlind: 15, bigBlind: 30 },
    { smallBlind: 25, bigBlind: 50 },
    { smallBlind: 50, bigBlind: 100 },
  ],
  standard: [
    { smallBlind: 5, bigBlind: 10 },
    { smallBlind: 10, bigBlind: 20 },
    { smallBlind: 15, bigBlind: 30 },
    { smallBlind: 25, bigBlind: 50 },
    { smallBlind: 50, bigBlind: 100 },
    { smallBlind: 75, bigBlind: 150 },
    { smallBlind: 100, bigBlind: 200 },
    { smallBlind: 150, bigBlind: 300 },
  ],
  deep: [
    { smallBlind: 5, bigBlind: 10 },
    { smallBlind: 10, bigBlind: 20 },
    { smallBlind: 15, bigBlind: 30 },
    { smallBlind: 20, bigBlind: 40 },
    { smallBlind: 25, bigBlind: 50 },
    { smallBlind: 50, bigBlind: 100 },
    { smallBlind: 75, bigBlind: 150 },
    { smallBlind: 100, bigBlind: 200 },
    { smallBlind: 150, bigBlind: 300 },
    { smallBlind: 200, bigBlind: 400 },
  ],
};

export const BlindScheduleConfig = memo(function BlindScheduleConfig({ value, onChange }: BlindScheduleConfigProps) {
  const [enabled, setEnabled] = useState(value !== null);
  const [mode, setMode] = useState<"Time" | "Hands">(value?.mode ?? "Time");
  const [intervalMinutes, setIntervalMinutes] = useState(value?.intervalMinutes ?? 10);
  const [intervalHands, setIntervalHands] = useState(value?.intervalHands ?? 10);
  const [levels, setLevels] = useState<BlindLevelDto[]>(value?.levels ?? PRESETS.standard);

  const toggleEnabled = () => {
    const next = !enabled;
    setEnabled(next);
    if (next) {
      onChange({ levels, mode, intervalMinutes, intervalHands });
    } else {
      onChange(null);
    }
  };

  const update = (newLevels?: BlindLevelDto[], newMode?: "Time" | "Hands", newMins?: number, newHands?: number) => {
    const l = newLevels ?? levels;
    const m = newMode ?? mode;
    const mi = newMins ?? intervalMinutes;
    const h = newHands ?? intervalHands;
    onChange({ levels: l, mode: m, intervalMinutes: mi, intervalHands: h });
  };

  const applyPreset = (key: string) => {
    const preset = PRESETS[key];
    if (!preset) return;
    setLevels(preset);
    if (enabled) update(preset);
  };

  const addLevel = () => {
    const last = levels[levels.length - 1];
    const newLevel: BlindLevelDto = {
      smallBlind: (last?.bigBlind ?? 100),
      bigBlind: (last?.bigBlind ?? 100) * 2,
    };
    const newLevels = [...levels, newLevel];
    setLevels(newLevels);
    if (enabled) update(newLevels);
  };

  const removeLevel = (idx: number) => {
    if (levels.length <= 1) return;
    const newLevels = levels.filter((_, i) => i !== idx);
    setLevels(newLevels);
    if (enabled) update(newLevels);
  };

  const updateLevel = (idx: number, field: "smallBlind" | "bigBlind" | "ante", val: number) => {
    const newLevels = levels.map((l, i) => i === idx ? { ...l, [field]: val } : l);
    setLevels(newLevels);
    if (enabled) update(newLevels);
  };

  return (
    <div
      className="rounded-2xl p-4 space-y-3"
      style={{ background: "var(--poker-surface-card)", border: "2px solid var(--poker-border-purple)" }}
    >
      <button
        onClick={toggleEnabled}
        className="flex items-center justify-between w-full"
      >
        <span className="text-xs font-medium" style={{ color: "var(--poker-text-dim)" }}>Blind Schedule</span>
        <div className={`poker-toggle ${enabled ? "poker-toggle--on" : "poker-toggle--off"}`}>
          <div className="poker-toggle-knob" />
        </div>
      </button>

      {enabled && (
        <>
          <div className="flex gap-2">
            {(["Time", "Hands"] as const).map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); update(undefined, m); }}
                className={`flex-1 h-10 text-xs font-semibold poker-btn-game poker-btn-game--sm ${mode === m ? "poker-btn-game--orange" : "poker-btn-game--dark"}`}
              >
                {m}
              </button>
            ))}
          </div>

          <div className="space-y-1">
            <label className="poker-label">
              {mode === "Time" ? "Interval (minutes)" : "Interval (hands)"}
            </label>
            <input
              type="number"
              value={mode === "Time" ? intervalMinutes : intervalHands}
              onChange={(e) => {
                const v = parseInt(e.target.value) || 1;
                if (mode === "Time") { setIntervalMinutes(v); update(undefined, undefined, v); }
                else { setIntervalHands(v); update(undefined, undefined, undefined, v); }
              }}
              className="w-full h-10 px-3 text-sm poker-input"
            />
          </div>

          <div className="flex gap-2">
            {[
              { key: "quick", label: "Quick (5)" },
              { key: "standard", label: "Standard (8)" },
              { key: "deep", label: "Deep (10)" },
            ].map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                className="flex-1 h-9 text-[10px] font-medium poker-btn-game poker-btn-game--sm poker-btn-game--dark"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="space-y-1.5 max-h-[200px] overflow-y-auto poker-scroll">
            {levels.map((level, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 text-xs"
              >
                <span className="w-5 text-center font-mono" style={{ color: "var(--poker-text-dim)" }}>{idx + 1}</span>
                <input
                  type="number"
                  value={level.smallBlind}
                  onChange={(e) => updateLevel(idx, "smallBlind", parseInt(e.target.value) || 0)}
                  className="w-20 h-9 px-2 text-center text-xs poker-input"
                />
                <span style={{ color: "var(--poker-text-dim)" }}>/</span>
                <input
                  type="number"
                  value={level.bigBlind}
                  onChange={(e) => updateLevel(idx, "bigBlind", parseInt(e.target.value) || 0)}
                  className="w-20 h-9 px-2 text-center text-xs poker-input"
                />
                {levels.length > 1 && (
                  <button
                    onClick={() => removeLevel(idx)}
                    className="text-sm" style={{ color: "var(--poker-fold)" }}
                  >
                    &times;
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={addLevel}
            className="w-full h-10 text-xs font-medium poker-btn-game poker-btn-game--sm poker-btn-game--dark"
          >
            + Add Level
          </button>
        </>
      )}
    </div>
  );
});
